/**
 * Canada offering (campaign) page content.
 *
 * Copy is reproduced verbatim from the "StartEngine Private Access Fund —
 * Offering" handoff. Legal and terms copy is compliance-reviewed — do not
 * paraphrase it.
 */

export const OFFERING_SECTION_IDS = {
  overview: 'overview',
  about: 'about',
  terms: 'terms',
} as const;

export const OFFERING_TABS = [
  { label: 'Overview', href: `#${OFFERING_SECTION_IDS.overview}` },
  { label: 'About', href: `#${OFFERING_SECTION_IDS.about}` },
  { label: 'Terms', href: `#${OFFERING_SECTION_IDS.terms}` },
];

export const OFFERING_HERO = {
  badge: 'Now Investing',
  seriesChip: 'LAMBDA LABS CANADA 1 · MEMBERSHIP INTEREST',
  eyebrow: 'AI Infrastructure · Available in Canada',
  title: 'Invest in Lambda Labs Canada 1. ',
  titleEm:
    'Access to a leading AI infrastructure company, made available to eligible Canadian investors.',
  lead: 'Lambda Labs Canada 1 offers eligible Canadian investors exposure to Lambda, Inc. (“Lambda Labs”) — a leading AI infrastructure and GPU cloud company — through a membership-interest series. In Canada, the offering is managed and distributed by Belco Private Capital Inc. under available prospectus exemptions.',
  ctaLabel: 'Book a Call',
  ctaNote:
    'Belco Private Capital Inc. has been retained as investment fund manager, portfolio manager and exempt market dealer in Canada. This investment is speculative, illiquid, and involves a high degree of risk, including the possible loss of your entire investment. Nothing on this page is an offer to sell securities.',
  imageSrc: '/homepage-v3/canada/lambda-hero.png',
  imageAlt: 'Lambda Labs — Series 18-4 membership interest offering',
  statusLabel: 'Status',
  statusValue: 'Accepting subscriptions',
} as const;

export interface OfferingReason {
  title: string;
  desc: string;
}

export const OFFERING_REASONS: OfferingReason[] = [
  {
    title: 'Access to a leading AI infrastructure company',
    desc: 'The offering is designed to give eligible Canadian investors exposure to Lambda, Inc. (“Lambda Labs”), a GPU cloud and AI infrastructure company, through a membership-interest series rather than a direct share purchase.',
  },
  {
    title: 'Managed by a registered Canadian firm',
    desc: 'In Canada, the Fund is managed and distributed by Belco Private Capital Inc., which acts as investment fund manager, portfolio manager and exempt market dealer.',
  },
  {
    title: 'A dedicated dealing representative',
    desc: 'Before you commit, a registered dealing representative with Belco reviews your eligibility and discusses whether the Fund is suitable for you.',
  },
];

export const OFFERING_ABOUT_PARAGRAPHS = [
  'Lambda Labs Canada 1 is a private offering that seeks to provide eligible Canadian investors with exposure to Lambda, Inc. (“Lambda Labs”) — a GPU cloud and AI infrastructure company of the kind typically available only to institutions and large venture funds before a public listing. Investors subscribe for membership interests in a dedicated series, which in turn holds an interest in Lambda Labs.',
  'This offering is for membership interests in STARTENGINE CA – 01 LIMITED PARTNERSHIP (“Lambda Labs Canada 1”), which will own Series Seed Preferred of Lambda, Inc. You are not buying Lambda Labs shares directly, and Lambda Labs is not participating in, or endorsing, this offering.',
];

export const OFFERING_OPPORTUNITY_PARAGRAPHS = [
  'Companies are staying private longer than they once did, and a meaningful part of their growth can occur before they ever list publicly. Historically, access to that stage was largely limited to venture capital firms and institutional investors.',
  'The Fund is intended to make that phase accessible to eligible Canadian investors in a structured, professionally managed way — with eligibility and suitability handled by a registered exempt market dealer. Private investments are speculative, illiquid, and involve a high degree of risk; most early-stage and pre-IPO companies do not achieve an IPO or other liquidity event.',
];

export const OFFERING_ELIGIBILITY_INTRO =
  'Belco Private Capital Inc. has been retained as the portfolio manager, investment fund manager and exempt market dealer to provide StartEngine and related and connected issuers with all services requiring such registrations in Canada.';

export interface OfferingTerm {
  label: string;
  value: string;
}

export const OFFERING_TERMS: OfferingTerm[] = [
  { label: 'Offering', value: 'Lambda Labs Canada 1' },
  {
    label: 'Issuer',
    value: 'STARTENGINE CA – 01 LIMITED PARTNERSHIP (“Lambda Labs Canada 1”)',
  },
  { label: 'Securities Offered', value: 'Membership interests' },
  { label: 'Underlying', value: 'Series Seed Preferred of Lambda, Inc.' },
  { label: 'Offering Basis', value: 'Prospectus exemptions (Canada)' },
  { label: 'Affiliate Price Per Security', value: '$48.00 USD' },
  { label: 'Price Per Security', value: '$67.50 USD' },
  { label: 'Minimum Investment', value: '$24,975.00 USD' },
  { label: 'Eligibility', value: 'Eligible investors, subject to suitability review' },
  { label: 'Investment Fund Manager', value: 'Belco Private Capital Inc.' },
  { label: 'Portfolio Manager', value: 'Belco Private Capital Inc.' },
  { label: 'Exempt Market Dealer', value: 'Belco Private Capital Inc.' },
  { label: 'Liquidity', value: 'Illiquid — long-term investment' },
];

export const OFFERING_TERMS_FOOTNOTE =
  "The above is a summary for general information only and is qualified in its entirety by the Fund's offering documents and subscription agreement, which should be read in their entirety before investing.";

export interface OfferingStep {
  lead: string;
  desc: string;
}

export const OFFERING_STEPS: OfferingStep[] = [
  {
    lead: 'Book a call.',
    desc: 'Speak with Warren Aarons, a registered dealing representative with Belco, about the Fund and your goals.',
  },
  {
    lead: 'Confirm eligibility & suitability.',
    desc: 'Belco assesses whether the Fund is suitable for you and confirms you qualify under an available prospectus exemption.',
  },
  {
    lead: 'Review documents & subscribe.',
    desc: 'Read the offering documents and subscription agreement in full, then complete the required documentation to subscribe.',
  },
];

export const OFFERING_RISKS = [
  'Belco Private Capital Inc. has been retained as the portfolio manager, investment fund manager and exempt market dealer to provide StartEngine and related and connected issuers with all services requiring such registrations in Canada. For offering documents and subscription information, and to discuss whether a StartEngine investment available in Canada is suitable for you, please contact Warren Aarons, a registered dealing representative with Belco, at warren@belcopc.com.',
  'Under no circumstances should this information be construed as an offering of any securities for sale directly or indirectly in any province or territory of Canada. Any offering is made only pursuant to the relevant offering documents together with the relevant subscription agreement, both of which should be read in their entirety. No offer to sell securities will be made prior to receipt of these documents by the offeree, and no offer to purchase securities will be accepted prior to completion of all appropriate documentation. Neither the Ontario Securities Commission nor any other securities regulatory authority of any Canadian jurisdiction has passed upon the accuracy or adequacy of this information material, and any representation to the contrary is unlawful. Results may differ materially from those described herein, and there may be factors and risks that cause actions, events or results not to be as anticipated, estimated or intended. You should not place undue reliance on past performance or forward-looking information contained herein.',
  'This offering is for membership interests in STARTENGINE CA – 01 LIMITED PARTNERSHIP (“Lambda Labs Canada 1”), which will own Series Seed Preferred of Lambda, Inc. (“Lambda Labs”). You are not buying Lambda Labs shares directly. Lambda Labs is not participating in, or endorsing, this offering, and its name and logo are used solely to identify the offering.',
  'Investing in private company securities is not suitable for all investors. An investment in private company securities is highly speculative and involves a high degree of risk, including the possible loss of your entire investment. It should only be considered a long-term, illiquid investment. You must be prepared to withstand a total loss of your investment.',
  'In Canada, StartEngine has retained Belco Private Capital Inc. (Belco) to perform investment fund manager, portfolio manager and exempt market dealer services. Belco is an independent contractor and is separate and distinct from the StartEngine group of companies. Each StartEngine company and Belco is liable only for its own acts or omissions and not those of each other.',
];

export const OFFERING_ASIDE_STATS = [
  { label: 'Minimum', value: '$24,975.00 USD' },
  { label: 'Structure', value: 'Membership' },
  { label: 'Basis', value: 'Exempt market' },
  { label: 'Jurisdiction', value: 'Canada' },
];

export const OFFERING_CONTACT = {
  heading: 'Have questions?',
  name: 'Warren Aarons, Belco Private Capital',
} as const;
